import React, { type ComponentProps, useCallback } from 'react';
import {
  type LayoutChangeEvent,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { theme } from '@garnish/constants';
import { LinearGradient } from 'expo-linear-gradient';

import { type Product } from '../utils';
import { OrderPlaybackFadeInView } from './OrderPlaybackFadeInView';
import { OrderPlaybackLineItem } from './OrderPlaybackLineItem';

export const OrderPlaybackLineItems = (props: OrderPlaybackLineItemsProps) => {
  const { fadeIn, products } = props;

  // ─── Layout Events ───────────────────────────────────────────────────

  const scrollViewHeight = useSharedValue(0);
  const handleScrollViewLayout = useCallback(
    (event: LayoutChangeEvent) => {
      scrollViewHeight.value = event.nativeEvent.layout.height;
    },
    [scrollViewHeight],
  );

  const contentHeight = useSharedValue(0);
  const handleContentLayout = useCallback(
    (event: LayoutChangeEvent) => {
      contentHeight.value = event.nativeEvent.layout.height;
    },
    [contentHeight],
  );

  // ─── Gradient Visibility ─────────────────────────────────────────────

  const gradientAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: contentHeight.value > scrollViewHeight.value ? 1 : 0,
    }),
    [contentHeight, scrollViewHeight],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <ScrollView
        style={styles.scrollViewContainer}
        contentContainerStyle={styles.scrollViewContent}
        onLayout={handleScrollViewLayout}
      >
        <View style={styles.scrollViewContent} onLayout={handleContentLayout}>
          {products.map((product) => (
            <OrderPlaybackLineItem
              key={product.id}
              name={product.name}
              quantity={product.quantity}
              imageUrl={product.imageUrl}
            />
          ))}
        </View>
      </ScrollView>

      <OrderPlaybackFadeInView fadeIn={fadeIn}>
        <Animated.View style={gradientAnimatedStyle}>
          <LinearGradient
            locations={[0, 1]}
            pointerEvents="none"
            style={styles.gradient}
            colors={GRADIENT_COLORS}
          />
        </Animated.View>
      </OrderPlaybackFadeInView>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  scrollViewContainer: {
    flex: 1,
    flexBasis: 0, // without this flex basis, the scroll view will expand and push the footer out of view.
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  gradient: {
    position: 'absolute',
    height: 15,
    top: -14,
    right: -theme.spacing['4'],
    left: -theme.spacing['4'],
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const GRADIENT_COLORS: ComponentProps<typeof LinearGradient>['colors'] = [
  theme.colors.OPACITY.DARK_KALE.TRANSPARENT,
  theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
];

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderPlaybackLineItemsProps = {
  fadeIn: number;
  products: readonly Product[];
};
